import type { IngestionConfirmationStatus, TransactionType } from '@dealstack/prisma-client';
import type { ActorRef } from '@dealstack/shared/lib/dealstackAi/actor';
import type { CorrectionKind } from '@dealstack/shared/lib/dealstackAi/correctionKind';
import type { RegisterVerdictCounts } from '@dealstack/shared/lib/dealstackAi/documents';
import { type ReasonCode, SELECT_BLOCK_CODES } from '@dealstack/shared/lib/dealstackAi/reasonCodes';
import type { PlacementCount } from '@dealstack/shared/lib/dealstackAi/rowPlacement';
import type { TransactionListFilter } from '@dealstack/shared/lib/dealstackAi/transactionListFilter';
import type { TransactionStatus } from '@dealstack/shared/lib/dealstackAi/transactionStatus';

/** What the confirmation lists before anything runs. */
export interface ConfirmSummary {
	rowCount: number;
	byDecision: Record<'create' | 'attach' | 'replace', number>;
	byType: Partial<Record<TransactionType, number>>;
	byStatus: Partial<Record<TransactionStatus, number>>;
	reasons: Partial<Record<ReasonCode, number>>;
	/** "3/3" → rows. */
	checks: Record<string, number>;
	entitiesToCreate: { securities: number; people: number };
	datesAssumed: number;
	/** Rows whose date the reviewer entered. */
	datesEntered: number;
	/** Rows dated from unanimous siblings, each confirmed. */
	datesFromSibling: number;
	fxConverted: number;
	/** Rows whose market rate the reviewer overrode. */
	fxOverridden: number;
	/** Lines collapsed under the rows as extra evidence. */
	duplicateLinesCollapsed: number;
	/** Rows that are corrections, by kind, and rows confirmed in one click. */
	corrections: number;
	correctionsByKind: Partial<Record<CorrectionKind, number>>;
	oneClickConfirmations: number;
	/** Rows by programme placement. */
	placements: PlacementCount[];
}

/**
 * The confirm's preview: the selected rows' summary, and the open rows in the selection's scope it leaves staged for
 * want of a cost. The scope's quick filter and reason are set aside, since an uncosted row is never ready.
 */
export interface ConfirmPreview extends ConfirmSummary {
	uncostedRowIds: string[];
	/** The rows' placements as previewed; a confirm sending it is refused once they change. */
	placementDigest: string;
}

/** One row (its own Confirm) or the rows selected in the active filter. */
export interface ConfirmRowsRequest {
	rowIds: string[];
	/** The filter the rows were selected under. */
	selectionScope: TransactionListFilter;
	/** Kept in the ingestion log. */
	note?: string;
	/** The preview's `placementDigest`; when sent, a placement changed since the preview is a 409. */
	placementDigest?: string;
}

/** The codes a confirm refusal names besides row reasons and placement codes; the page labels each one. */
export const CONFIRM_ISSUE_CODES = [
	...SELECT_BLOCK_CODES,
	/** The row's security or conversion target is ignored. */
	'security_ignored',
	'row_outside_selection_scope',
	/** The attach or replace target is no longer a live ledger row. */
	'ledger_row_gone',
	/** The chosen twin is no longer in its set. */
	'twin_not_in_set',
] as const;
export type ConfirmIssueCode = (typeof CONFIRM_ISSUE_CODES)[number];

/** How one confirm ended. */
export type ConfirmationStatus = IngestionConfirmationStatus;

export const CONFIRM_JOB_PHASES = [
	'queued',
	'waiting_for_locks',
	'preparing',
	'linking_evidence',
	'finalizing',
	'creating_entities',
	'writing',
	'folding',
	'diffing',
	'committing',
	'rolled_back',
	'done',
] as const;
export type ConfirmJobPhase = (typeof CONFIRM_JOB_PHASES)[number];

/** The confirm job's progress, kept outside its database transaction. */
export interface ConfirmJob {
	/** Attempt-local counters; absent on historical confirmations. */
	attemptStartedAt?: string;
	attempt?: number;
	sequence?: number;
	phaseStartedAt?: string;
	lastProgressAt?: string;
	workerHeartbeatAt?: string;
	phase: ConfirmJobPhase;
	rowsDone: number;
	rowsTotal: number;
	startedAt: string | null;
	finishedAt: string | null;
}

/**
 * The validation codes the deal's fold emits (`buildDecoratedTransactionsQuery`). The confirm captures them before
 * its write and diffs them after the fold; any code that is new on a transaction blocks.
 */
export const LEDGER_VALIDATION_ERRORS = [
	'TRANSACTION_TYPE_REQUIRED',
	'SOURCE_ENTITY_REQUIRED',
	'SOURCE_MUST_BE_ENTITY',
	'DESTINATION_MUST_BE_ENTITY',
	'DESTINATION_ENTITY_REQUIRED',
	'SOURCE_DESTINATION_SAME',
	'PAIR_LEG_MISSING',
	'NO_SOURCE_BALANCE',
	'OVERSOLD',
	'NEGATIVE_SHARE_CAPITAL',
] as const;
export type LedgerValidationError = (typeof LEDGER_VALIDATION_ERRORS)[number];

export interface InvalidPartition {
	holderId: string;
	securityId: string;
	mipId: string | null;
	heldForId: string | null;
	kind: 'oversold' | 'negative';
	/** The codes the partition's transactions newly carry. */
	errors: LedgerValidationError[];
	/** The confirm's rows upstream of the partition; the page marks each `fold_invalid`. */
	rowIds: string[];
	/** The live ledger rows the new errors sit on. */
	transactionIds: string[];
}

/**
 * The batch's registers against the ledger as at upload plus the confirm's own rows, read on every confirm. Null
 * when the batch holds no register.
 */
export interface RegisterTieSummary {
	registers: number;
	positions: number;
	literal: RegisterVerdictCounts;
	converted: RegisterVerdictCounts;
	surpluses: number;
	nominalOnly: number;
	currencyDiffers: number;
	/** Tie or explained over positions, 0–1, under whichever reading ties more; null with no position. */
	tieRate: number | null;
}

export interface FoldCheckResult {
	ranAt: string;
	/** Exactly the confirm's rows. */
	rowIds: string[];
	ok: boolean;
	newInvalidPartitions: InvalidPartition[];
	registerTies: RegisterTieSummary | null;
}

/**
 * An attach or replace target as the confirm accepted it, with the ledger row's version at that moment. Kept on the
 * confirmation so the job refuses a target that moved between the confirm and the write.
 */
export interface ConfirmTarget {
	rowId: string;
	transactionId: string;
	/** ISO 8601. */
	updatedAt: string;
}

export interface ConfirmResult {
	confirmationId: string;
	written: { created: number; attached: number; replaced: number };
	transactionIds: string[];
}

/** One confirm by one deal admin. */
export interface Confirmation {
	id: string;
	batchId: string;
	dealId: string;
	status: ConfirmationStatus;
	rowIds: string[];
	selectionScope: TransactionListFilter;
	summary: ConfirmSummary;
	confirmedBy: ActorRef;
	confirmedAt: string;
	note: string | null;
	job: ConfirmJob;
	/** Set once the fold has been diffed; on `blocked` it names the rows. */
	foldCheck: FoldCheckResult | null;
	/** Set on `written`. */
	result: ConfirmResult | null;
	/** Set on `failed`. */
	error: string | null;
}

/** Reject out-of-order telemetry, including delayed snapshots from an earlier retry. */
export function newestConfirmJob<T extends Pick<ConfirmJob, 'attempt' | 'sequence'>>(
	current: T,
	candidate: T | null
): T {
	if (!candidate) {
		return current;
	}
	if ((candidate.attempt ?? 0) !== (current.attempt ?? 0)) {
		return (candidate.attempt ?? 0) > (current.attempt ?? 0) ? candidate : current;
	}
	return (candidate.sequence ?? 0) >= (current.sequence ?? 0) ? candidate : current;
}
